import moment from 'moment';
import 'moment-precise-range-plugin';

/**
 * Get difference between two dates.
 * Dates should be defined in a ISO 8601 format.
 *
 * @param from - From Date
 * @param to - To Date. Optional, if not specified, today is used.
 * @return {{years : number, months : number, days : number, hours : number, minutes : number}}
 */
export function getRange(from, to = null) {
  let toMoment;
  if (to !== null) {
    toMoment = moment.utc(to);
  } else {
    toMoment = moment.utc();
  }

  const fromMoment = moment.utc(from);

  let result = moment.preciseDiff(fromMoment, toMoment, true);
  delete result.firstDateWasLater;
  delete result.seconds;

  return result;
}

export function countdown(from, to = null) {
  const elapsedTime = getRange(from, to);
  const values = [];
  let label = '';
  if (elapsedTime.years > 0) {
    label = elapsedTime.years > 1 ? 'years' : 'year';
    values.push(`${elapsedTime.years} ${label}`);
  }
  if (elapsedTime.months > 0) {
    label = elapsedTime.months > 1 ? 'months' : 'month';
    values.push(`${elapsedTime.months} ${label}`);
  }
  // Only show two attributes from the range value
  if (elapsedTime.days > 0 && values.length < 2) {
    label = elapsedTime.days > 1 ? 'days' : 'day';
    values.push(`${elapsedTime.days} ${label}`);
  }
  if (elapsedTime.hours > 0 && values.length < 2) {
    label = elapsedTime.hours > 1 ? 'hours' : 'hour';
    values.push(`${elapsedTime.hours} ${label}`);
  }
  if (elapsedTime.minutes > 0 && values.length < 2) {
    label = elapsedTime.minutes > 1 ? 'minutes' : 'minute';
    values.push(`${elapsedTime.minutes} ${label}`);
  }

  return values.join(', ');
}

/**
 * Get first date form array of dates. Dates should be defined in a ISO 8601 format.
 * @param arrayOfDates
 * @return {string} Date as YYYY-MM-DD format or null if array is empty
 */
export function getFirstDate(arrayOfDates) {
  let min = null;
  if (
    arrayOfDates === undefined ||
    arrayOfDates === null ||
    arrayOfDates.length === 0
  ) {
    return min;
  }

  arrayOfDates.forEach((date) => {
    let dateMoment = moment(date);
    if (min === null || min.isAfter(dateMoment)) {
      min = dateMoment;
    }
  });

  return min.format('YYYY-MM-DD');
}

/**
 * Get last date form array of dates. Dates should be defined in a ISO 8601 format.
 * @param arrayOfDates
 * @return {string} Date as YYYY-MM-DD format or null if array is empty
 */
export function getLastDate(arrayOfDates) {
  let max = null;
  if (
    arrayOfDates === undefined ||
    arrayOfDates === null ||
    arrayOfDates.length === 0
  ) {
    return max;
  }

  arrayOfDates.forEach((date) => {
    let dateMoment = moment(date);
    if (max === null || max.isBefore(dateMoment)) {
      max = dateMoment;
    }
  });

  return max.format('YYYY-MM-DD');
}

const yesterday = moment().subtract(1, 'day');

/**
 * Validate a date against yesterday. To be used to validate dates in DatetimePicker component.
 * @param date
 * @return {*|boolean}
 */
export function todayValidation(date) {
  return date.isAfter(yesterday);
}

/**
 * Validates if a date is same or before maxDate. To be used to validate dates in DatetimePicker component.
 * @param date
 * @param maxDate
 * @return {*|boolean}
 */
export function sameOrBeforeDateValidation(date, maxDate) {
  return maxDate ? date.isSameOrBefore(moment(maxDate)) : true;
}

/**
 * Validates if a date is same or after minDate. To be used to validate dates in DatetimePicker component.
 * @param date
 * @param minDate
 * @return {*|boolean}
 */
export function sameOrAfterDateValidation(date, minDate) {
  return minDate ? date.isSameOrAfter(moment(minDate)) : true;
}

/**
 * Validate a date is before today. To be used to validate dates in DatetimePicker component.
 * @param date
 * @return {*|boolean}
 */
export function beforeTodayValidation(date) {
  return date.isBefore(moment());
}

export function sameOrBeforeTodayValidation(date) {
  return date.isSameOrBefore(moment());
}

/**
 * Get the next date of a given day (from 0 to 6). Eg. The next Tuesday is nextDay(2)
 * @param day
 * @return {string} Date as YYYY-MM-DD format or null if array is empty
 */
export function nextDay(day) {
  const today = moment().isoWeekday();
  let nextD = null;

  if (today <= day) {
    nextD = moment().isoWeekday(day);
  } else {
    nextD = moment().add(1, 'weeks').isoWeekday(day);
  }

  return nextD.format('YYYY-MM-DD');
}

/**
 * Get the previous date of a given day (from 0 to 6). Eg. The previous Tuesday is prevDay(2)
 * @param day
 * @return {string} Date as YYYY-MM-DD format or null if array is empty
 */
export function prevDay(day) {
  const today = moment().isoWeekday();
  let prevD = null;

  if (today >= day) {
    prevD = moment().isoWeekday(day);
  } else {
    prevD = moment().add(-1, 'weeks').isoWeekday(day);
  }

  return prevD.format('YYYY-MM-DD');
}

export function utcTime(datetime) {
  return (
    datetime && moment.utc(datetime, 'YYYY-MM-DD hh:mm A').format('hh:mm A')
  );
}



// WEBPACK FOOTER //
// ./src/services/utils/date-util.js